// ─────────────────────────────────────────────────────────────────────────────
// StartupSweepOverlay.jsx
// Visual presentation layer for the Startup Readiness Sweep.
// Renders on top of the live dashboard (position: fixed, pointer-events: none
// except for the dismiss area).
//
// Pipeline stations: Receiving → Material Issue → Fabrication → QA/NCR → Shipping
// The tracer line sweeps left-to-right driven by tracerPct (0–100).
// KPI tiles and the risk card appear once their phase is active.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect } from 'react';
import { Box, Stack, Typography, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SweepPhase } from './StartupSweepController';

// ── Station definitions ────────────────────────────────────────────────────
const STATIONS = [
  { id: 'receiving',   label: 'Receiving',       short: 'RCV', color: '#1976d2' },
  { id: 'issue',       label: 'Material Issue',  short: 'ISS', color: '#7b1fa2' },
  { id: 'fabrication', label: 'Fabrication',     short: 'FAB', color: '#2e7d32' },
  { id: 'qanc',        label: 'QA / NCR',        short: 'QA',  color: '#e65100' },
  { id: 'shipping',    label: 'Shipping',         short: 'SHP', color: '#0277bd' },
];

// Which station the tracer has reached given tracerPct (0–100)
function activeStationIndex(tracerPct) {
  const perStation = 100 / STATIONS.length;
  return Math.min(STATIONS.length - 1, Math.floor(tracerPct / perStation));
}

// ── Severity colours ───────────────────────────────────────────────────────
const SEVERITY_COLOR = {
  error:   '#d32f2f',
  warning: '#ed6c02',
  success: '#2e7d32',
  info:    '#0288d1',
};

// ── Component ─────────────────────────────────────────────────────────────
export default function StartupSweepOverlay({
  phase,
  tracerPct,
  snapshot,
  priority,       // from LandingPriorityResolver.fromSnapshot()
  onFastForward,  // called when user clicks overlay
}) {
  const isVisible  = phase !== SweepPhase.IDLE && phase !== SweepPhase.SETTLED;
  const isComplete = phase === SweepPhase.COMPLETE;
  const showTracer = [
    SweepPhase.TRACER, SweepPhase.KPI_REVEAL,
    SweepPhase.MILESTONE, SweepPhase.RISK, SweepPhase.COMPLETE,
  ].includes(phase);
  const showKpi    = [
    SweepPhase.KPI_REVEAL, SweepPhase.MILESTONE,
    SweepPhase.RISK, SweepPhase.COMPLETE,
  ].includes(phase);
  const showRisk   = [SweepPhase.RISK, SweepPhase.COMPLETE].includes(phase);
  const showBrand  = phase !== SweepPhase.IDLE && phase !== SweepPhase.SHELL;

  const activeIdx = activeStationIndex(tracerPct);
  const severityColor = priority ? SEVERITY_COLOR[priority.severity] || SEVERITY_COLOR.info : SEVERITY_COLOR.info;

  // Global input → fast-forward
  useEffect(() => {
    if (!isVisible || isComplete) return;
    const handler = () => onFastForward && onFastForward();
    window.addEventListener('keydown', handler, { once: true });
    window.addEventListener('touchstart', handler, { once: true });
    return () => {
      window.removeEventListener('keydown', handler);
      window.removeEventListener('touchstart', handler);
    };
  }, [isVisible, isComplete, onFastForward]);

  if (!isVisible) return null;

  return (
    <Box
      onClick={onFastForward}
      sx={{
        position:       'fixed',
        inset:          0,
        zIndex:         1400,
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'center',
        justifyContent: 'center',
        bgcolor:        alpha('#0a0f1e', 0.88),
        backdropFilter: 'blur(6px)',
        cursor:         'pointer',
        // Fade in on mount, fade out on COMPLETE
        opacity:        isComplete ? 0 : 1,
        transition:     isComplete ? 'opacity 200ms ease-out' : 'opacity 120ms ease-in',
        userSelect:     'none',
      }}
      aria-hidden="true"
    >
      {/* ── Brand ignition ─────────────────────────────────────────── */}
      <Box
        sx={{
          mb: 3,
          opacity:    showBrand ? 1 : 0,
          transform:  showBrand ? 'scale(1) translateY(0)' : 'scale(0.92) translateY(8px)',
          transition: 'opacity 120ms ease-out, transform 120ms ease-out',
          textAlign:  'center',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color:       'white',
            fontWeight:  800,
            letterSpacing: 1.5,
            fontSize:    '1.4rem',
          }}
        >
          SHOP FLOOR MANAGER
        </Typography>
        <Typography variant="caption" sx={{ color: alpha('#fff', 0.45), letterSpacing: 3 }}>
          READINESS SWEEP
        </Typography>
      </Box>

      {/* ── Station pipeline ───────────────────────────────────────── */}
      <Box
        sx={{
          width:   '100%',
          maxWidth: 700,
          px:       3,
          mb:       2,
          opacity:  showBrand ? 1 : 0,
          transition: 'opacity 100ms ease-out',
        }}
      >
        {/* Station labels row */}
        <Stack direction="row" justifyContent="space-between" mb={0.75}>
          {STATIONS.map((s, i) => (
            <Typography
              key={s.id}
              variant="caption"
              sx={{
                color:      i <= activeIdx && showTracer ? 'white' : alpha('#fff', 0.35),
                fontWeight: i === activeIdx && showTracer ? 700 : 400,
                fontSize:   '0.65rem',
                letterSpacing: 0.5,
                transition: 'color 120ms',
                width:      `${100 / STATIONS.length}%`,
                textAlign:  'center',
              }}
            >
              {s.short}
            </Typography>
          ))}
        </Stack>

        {/* Track + tracer */}
        <Box sx={{ position: 'relative', height: 4, borderRadius: 2, bgcolor: alpha('#fff', 0.12) }}>
          {/* Tracer fill */}
          <Box
            sx={{
              position:     'absolute',
              left:         0,
              top:          0,
              height:       '100%',
              borderRadius: 2,
              width:        `${tracerPct}%`,
              background:   `linear-gradient(90deg, #1976d2, #7b1fa2, #2e7d32, #e65100, #0277bd)`,
              backgroundSize: '500% 100%',
              transition:   'width 16ms linear',
              boxShadow:    showTracer ? `0 0 8px 2px ${alpha('#1976d2', 0.7)}` : 'none',
            }}
          />

          {/* Station dots */}
          {STATIONS.map((s, i) => {
            const leftPct = (i / (STATIONS.length - 1)) * 100;
            const reached = i <= activeIdx && showTracer;
            return (
              <Box
                key={s.id}
                sx={{
                  position:     'absolute',
                  left:         `${leftPct}%`,
                  top:          '50%',
                  transform:    'translate(-50%, -50%)',
                  width:        reached ? 10 : 8,
                  height:       reached ? 10 : 8,
                  borderRadius: '50%',
                  bgcolor:      reached ? s.color : alpha('#fff', 0.25),
                  border:       `2px solid ${reached ? s.color : alpha('#fff', 0.15)}`,
                  boxShadow:    reached ? `0 0 6px ${alpha(s.color, 0.8)}` : 'none',
                  transition:   'all 120ms ease-out',
                  zIndex:       1,
                }}
              />
            );
          })}
        </Box>

        {/* Station full labels (below track) */}
        <Stack direction="row" justifyContent="space-between" mt={0.75}>
          {STATIONS.map((s, i) => (
            <Typography
              key={s.id}
              variant="caption"
              sx={{
                color:      i <= activeIdx && showTracer ? alpha('#fff', 0.75) : alpha('#fff', 0.18),
                fontSize:   '0.6rem',
                width:      `${100 / STATIONS.length}%`,
                textAlign:  'center',
                transition: 'color 120ms',
              }}
            >
              {s.label}
            </Typography>
          ))}
        </Stack>
      </Box>

      {/* ── KPI tiles ─────────────────────────────────────────────── */}
      {showKpi && snapshot && (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            mb:       2,
            opacity:  showKpi ? 1 : 0,
            transform: showKpi ? 'translateY(0)' : 'translateY(12px)',
            transition: 'opacity 180ms ease-out, transform 180ms ease-out',
          }}
        >
          {[
            { label: 'Receiving',    value: `${snapshot.receivingPct ?? '—'}%`,   color: '#1976d2' },
            { label: 'Issued',       value: `${snapshot.issuedPct    ?? '—'}%`,   color: '#7b1fa2' },
            { label: 'Fabrication',  value: `${snapshot.fabricationPct ?? '—'}%`, color: '#2e7d32' },
            { label: 'QC Pass',      value: `${snapshot.qcPassPct    ?? '—'}%`,   color: '#e65100' },
            { label: 'Open NCRs',    value: snapshot.openNcrs ?? '—',              color: snapshot.openNcrs > 0 ? '#d32f2f' : '#0277bd' },
          ].map((kpi) => (
            <Box
              key={kpi.label}
              sx={{
                px:          1.5,
                py:          1,
                borderRadius: 1.5,
                bgcolor:     alpha(kpi.color, 0.12),
                border:      `1px solid ${alpha(kpi.color, 0.3)}`,
                textAlign:   'center',
                minWidth:    72,
              }}
            >
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 800, lineHeight: 1 }}>
                {kpi.value}
              </Typography>
              <Typography variant="caption" sx={{ color: alpha('#fff', 0.5), fontSize: '0.58rem' }}>
                {kpi.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      {/* ── Priority risk card ────────────────────────────────────── */}
      {showRisk && priority && (
        <Box
          sx={{
            px:          2.5,
            py:          1.25,
            borderRadius: 2,
            border:      `1.5px solid ${alpha(severityColor, 0.6)}`,
            bgcolor:     alpha(severityColor, 0.10),
            boxShadow:   `0 0 16px ${alpha(severityColor, 0.25)}`,
            opacity:     showRisk ? 1 : 0,
            transform:   showRisk ? 'scale(1)' : 'scale(0.94)',
            transition:  'opacity 200ms ease-out, transform 200ms ease-out',
            maxWidth:    400,
            textAlign:   'center',
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={0.25}>
            <Chip
              label={priority.severity === 'success' ? '✓' : priority.severity === 'error' ? '✕' : '⚠'}
              size="small"
              sx={{
                bgcolor:    severityColor,
                color:      'white',
                fontWeight: 800,
                fontSize:   '0.7rem',
                height:     20,
                minWidth:   20,
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>
              {priority.label}
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: alpha('#fff', 0.55) }}>
            {priority.description}
          </Typography>
        </Box>
      )}

      {/* ── Dismiss hint ──────────────────────────────────────────── */}
      <Typography
        variant="caption"
        sx={{
          position:  'absolute',
          bottom:    24,
          color:     alpha('#fff', 0.20),
          fontSize:  '0.6rem',
          letterSpacing: 1,
        }}
      >
        CLICK ANYWHERE TO DISMISS
      </Typography>
    </Box>
  );
}
